import { SalesRep } from 'types/salerep';
import { createGenericCRUD, useUploadFileData } from './commonCrud';
import useSWR, { mutate } from 'swr';
import { Permit, UserList } from 'types/user';
import { fetcher, fetcherPost, fetcherSendRequest, fetcherWithMethod } from 'utils/axios';
import { handleApiError, showSuccess } from 'utils/errorHandler';
import { CrudEndpoints, DuplicateResponse } from 'types/common';
import { updateListCache } from './commonCrud';
import { useMemo } from 'react';

export const endpoints: CrudEndpoints = {
  key: 'UserManagement/User',
  getList: '/GetList',
  getListById: '/GetList/{ID}',
  getModal: '/',
  getLookupList: '/GetLookupList',
  insert: '/',
  update: '/',
  delete: '',
  getlookupPost: true
};

const userCRUD = createGenericCRUD<UserList>(endpoints);

export const useCheckDuplicateEmail = async (emailID: string, excludeID?: number): Promise<boolean> => {
  if (!emailID) return false;
  const endpoint = `${endpoints.key}/CheckDuplicateEmailID?EmailID=${encodeURIComponent(emailID)}${excludeID ? `&ExcludeID=${excludeID}` : ''}`

  try {
    const res = await fetcherWithMethod('GET', [endpoint, { method: 'GET' }]);
    return res as boolean;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export async function useUpdateProfilePic(file: File | null, userID: number, message?: string): Promise<any | null> {
  const result = await useUploadFileData<any>(`${endpoints.key}/UpdateUserProfileImage`,
    null,
    {
      FileName: file,
      userID: userID
    },
    { showSnackbar: false, method: 'POST' }
  );
  if (file === null) {
    // Clear out profile pic URL from cache when profile pic is removed
    mutate(`${endpoints.key}/GetUserProfileImageUrl/${userID}`, '', false);
  }

  await mutate(`${endpoints.key}/GetUserProfileImageUrl/${userID}`);

  return result;
}

// ==============================|| USER SEAL & SIGNATURE APIS ||============================== //

// Update User Seal
export async function useUpdateSeal(file: File | null, userID: number, message?: string): Promise<any | null> {
  const result = await useUploadFileData<any>(
    `${endpoints.key}/UpdateSeal/${userID}`,
    null,
    {
      sealFile: file
    },
    { showSnackbar: Boolean(message), method: 'PUT' }
  );

  if (file === null) {
    mutate(`${endpoints.key}/GetSeal/${userID}`, '', false);
  }

  await mutate(`${endpoints.key}/GetSeal/${userID}`);

  return result;
}

// Get User Seal
export function useGetSeal(id?: number | null) {
  return useSWR<string>(id ? `${endpoints.key}/GetSeal/${id}` : null, fetcher, {
    revalidateIfStale: true,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    onError: (err) => {
      handleApiError(err);
    }
  });
}

export async function getUserSeal(id: number): Promise<string> {
  return fetcher(`${endpoints.key}/GetSeal/${id}`);
}

// Update User Signature
export async function useUpdateSignature(file: File | null, userID: number, message?: string): Promise<any | null> {
  const result = await useUploadFileData<any>(
    `${endpoints.key}/UpdateSignature/${userID}`,
    null,
    {
      signatureFile: file
    },
    { showSnackbar: Boolean(message), method: 'PUT' }
  );

  if (file === null) {
    mutate(`${endpoints.key}/GetSignature/${userID}`, '', false);
  }

  await mutate(`${endpoints.key}/GetSignature/${userID}`);

  return result;
}

// Get User Signature
export function useGetSignature(id?: number | null) {
  return useSWR<string>(id ? `${endpoints.key}/GetSignature/${id}` : null, fetcher, {
    revalidateIfStale: true,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    onError: (err) => {
      handleApiError(err);
    }
  });
}

export async function getUserSignature(id: number): Promise<string> {
  return fetcher(`${endpoints.key}/GetSignature/${id}`);
}

// Update User License Number
export const useUpdateLicenseNo = async (data: { userID: number; licenseNo: string; updatedOn: string } | any) => {
  try {
    const response = await fetcherWithMethod('PUT', [`${endpoints.key}/UpdateLicenseNo`, { data }]);
    return response;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

// ============================================================================================= //

export function useGetProfileImageUrl(id: number) {
  return useSWR<string>(id ? `${endpoints.key}/GetUserProfileImageUrl/${id}` : null, fetcher, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    dedupingInterval: 10000,
    onError: (err) => {
      handleApiError(err);
    }
  });
}

export const useGetUsersWithFilters = (id?: number) => {
  let url = id ? `${endpoints.key}/${id}` : `${endpoints.key}${endpoints.getList}`;
  const { data, error, isLoading, mutate } = useSWR<UserList | UserList[]>(
    url,
    (url: string) => fetcherWithMethod('GET', url),
    {
      revalidateIfStale: true,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      onError: handleApiError
    }
  );

  return {
    data: data ?? [],
    error,
    isLoading,
    mutate,
    hasError: !!error,
    retry: () => mutate()
  };
};

export const useDisableTwoFA = async (userID: number) => {
  const response = await fetcherWithMethod('PATCH', `${endpoints.key}/DisableTwoFA/${userID}`);
  const listKey = `${endpoints.key}${endpoints.getList}`;
  const getByIdEndpoint = endpoints.getListById.replace('{ID}', userID.toString());
  const fullEndpoint = `${endpoints.key}${getByIdEndpoint}`;

  const fetchedItem = await fetcher(fullEndpoint);
  if (fetchedItem) {
    updateListCache(fetchedItem, listKey);
  }
  return response
};

export const useGetSalesRepLookupList = (params: any) => {
  const { data, error, isLoading, mutate } = useSWR<SalesRep[]>(
    params ? `${endpoints.key}/GetSalesRepLookupList` : null,
    (url: string) => fetcherWithMethod('POST', [url, { data: params }]),
    {
      revalidateIfStale: true,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      onError: handleApiError
    }
  );
  return {
    data: data ?? [],
    error,
    isLoading,
    mutate,
    hasError: Boolean(error),
    retry: () => mutate()
  };
};

export const useSendPasswordInvite = (contractorCompanyID: number) => fetcherSendRequest(`${endpoints.key}/SendPasswordInvite/${contractorCompanyID}`);

export const useUpdatePermitTypeConfiguration = async (userID: number, data: any) => {
  try {
    const response = await fetcherWithMethod('PUT', [`Settings/User/PermitTypeConfiguration/${userID}`, { data }]);
    showSuccess('Updated sucessfully');
    return response;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export const useGetPlanReviewPermitTypes = (userID: number) => {
  let url = userID ? `Settings/User/PermitTypeConfiguration/GetPlanReviewPermitTypes/${userID}` : null;
  const { data, error, isLoading, mutate } = useSWR<Permit>(
    url,
    (url: string) => fetcherWithMethod('GET', url),
    {
      revalidateIfStale: true,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      onError: (err) => handleApiError(err)
    }
  );

  return {
    data: data ?? [],
    error,
    isLoading,
    mutate,
    hasError: !!error,
    retry: () => mutate()
  };
};

export const useGetInspectionPermitTypes = (userID: number) => {
  let url = userID ? `Settings/User/PermitTypeConfiguration/GetInspectionPermitTypes/${userID}` : null;
  const { data, error, isLoading, mutate } = useSWR<Permit>(
    url,
    (url: string) => fetcherWithMethod('GET', url),
    {
      revalidateIfStale: true,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      onError: (err) => handleApiError(err)
    }
  );

  return {
    data: data ?? [],
    error,
    isLoading,
    mutate,
    hasError: !!error,
    retry: () => mutate()
  };
};

export async function useInsertPlanReviewPermitType(data: { userID: number, municipalityID: number, permitTypeID: number }) {
  try {
    const response = await fetcherWithMethod('POST', [`Settings/User/PermitTypeConfiguration/InsertPlanReviewPermitType`, { data }]);

    return response;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
}

export const useUpdateActiveStatus = async (data: any) => {

  try {
    const response = await fetcherWithMethod('PUT', [`${endpoints.key}/UpdateActiveStatus`, { data }]);
    showSuccess('Updated successfully');
    return response;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};
export const useGetProjectSpecialistLookupList = (params: any) => {
  const { data, error, isLoading, mutate } = useSWR<any[]>(
    params ? `${endpoints.key}/GetProjectSpecialistLookupList` : null,
    (url: string) => fetcherWithMethod('POST', [url, { data: params }]),
    {
      revalidateIfStale: true,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      onError: handleApiError
    }
  );
  return {
    data: data ?? [],
    error,
    isLoading,
    mutate,
    hasError: Boolean(error),
    retry: () => mutate()
  };
};

export const useGetInspectorLookupList = (enabled = true) => {
  const endpoint = `${endpoints.key}/GetInspectorLookupList`;

  const { data, isLoading, error, isValidating, mutate } = useSWR(enabled ? [endpoint] : null, ([url]) => {
    return fetcherWithMethod('POST', [url, { data: { includeIDs: [] } }]);
  },
    {
      revalidateIfStale: true,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      onError: (err) => handleApiError(err)
    }
  );

  return useMemo(
    () => ({
      data: data || [],
      isLoading,
      error,
      mutate,
      isValidating,
      isEmpty: !isLoading && (!data || data.length === 0)
    }),
    [data, error, isLoading, isValidating, mutate]
  );
};

// ==============================|| PRIVATE PROVIDER LOOKUP API ||============================== //

// Get Private Provider Lookup List
export const useGetPrivateProviderLookupList = (includeIDs: number[] = []) => {
  const endpoint = `${endpoints.key}/GetPrivateProviderLookupList`;

  const { data, isLoading, error, isValidating, mutate } = useSWR(
    [endpoint, { data: { includeIDs } }],
    ([url, body]) => {
      return fetcherWithMethod('POST', [url, body]);
    },
    {
      revalidateIfStale: true,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      onError: (err) => handleApiError(err)
    }
  );

  return useMemo(
    () => ({
      data: data || [],
      isLoading,
      error,
      mutate,
      isValidating,
      isEmpty: !isLoading && (!data || data.length === 0)
    }),
    [data, error, isLoading, isValidating, mutate]
  );
};

// ============================================================================================= //


export const {
  useGetList: useGetUserList,
  useGetListByID: useGetUserListByID,
  useGetModal: useGetUserModal,
  useGetLookupList: useGetUserLookupList,
  useInsertRecord: useInsertUserRecord,
  useUpdateRecord: useUpdateUserRecord,
  useDeleteRecord: useDeleteUserRecord
} = userCRUD;

